





























































import Vue from "vue";
import {
  ConvenienceVerkaufFilter,
  ExportJob,
  ExportStatusResponse,
} from "@/models/ConvenienceVerkaufModel";
import { api } from "@/util/api";

export default Vue.extend({
  name: "ExportDialog",
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    filter: {
      type: Object as () => ConvenienceVerkaufFilter,
      required: true,
    },
  },
  data() {
    return {
      selectedFormat: "xlsx" as "xlsx" | "csv",
      jobId: null as string | null,
      jobStatus: null as ExportStatusResponse | null,
      isCreatingJob: false,
      isDownloading: false,
      pollInterval: null as number | null,
    };
  },
  computed: {
    isOpen: {
      get(): boolean {
        return this.show;
      },
      set(value: boolean) {
        this.$emit("update:show", value);
      },
    },
    progressPercent(): number {
      if (!this.jobStatus || this.jobStatus.total === 0) return 0;
      return (this.jobStatus.progress / this.jobStatus.total) * 100;
    },
    statusColor(): string {
      switch (this.jobStatus?.status) {
        case "done":
          return "green";
        case "error":
          return "red";
        case "running":
          return "blue";
        default:
          return "grey";
      }
    },
  },
  watch: {
    show(newVal) {
      if (!newVal) {
        this.stopPolling();
      }
    },
  },
  methods: {
    async startExport() {
      this.isCreatingJob = true;
      try {
        const response = await api.post<ExportJob>(
          "statistiken/verkauf/export",
          {
            filter: {
              Von: this.filter.von,
              Bis: this.filter.bis,
              Filiale: this.filter.filialen,
              Lieferant:
                this.filter.lieferanten && this.filter.lieferanten.length === 1
                  ? this.filter.lieferanten[0]
                  : undefined,
              Warengruppen: this.filter.warengruppen,
              NurVerkaufte: this.filter.nur_verkaufte,
            },
            format: this.selectedFormat,
          }
        );

        if (api.isErrorResponse(response.data)) {
          throw new Error("Export job could not be created");
        }

        this.jobId = response.data.id;
        this.jobStatus = {
          id: response.data.id,
          status: response.data.status,
          progress: response.data.progress,
          total: response.data.total,
        };

        this.startPolling();
      } catch (error) {
        console.error("Failed to create export job:", error);
        this.$root.$emit("show-error", "Fehler beim Erstellen des Exports");
      } finally {
        this.isCreatingJob = false;
      }
    },
    startPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
      }

      this.pollInterval = window.setInterval(() => {
        this.checkStatus();
      }, 500); // Poll every 500ms

      // Initial check
      this.checkStatus();
    },
    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },
    async checkStatus() {
      if (!this.jobId) return;

      try {
        const response = await api.get<ExportStatusResponse>(
          `statistiken/verkauf/export/${this.jobId}/status`
        );

        if (api.isErrorResponse(response.data)) return;
        this.jobStatus = response.data;

        if (
          this.jobStatus.status === "done" ||
          this.jobStatus.status === "error"
        ) {
          this.stopPolling();
        }
      } catch (error) {
        console.error("Failed to check status:", error);
      }
    },
    downloadFile() {
      if (!this.jobId) return;

      this.isDownloading = true;
      const extension = this.selectedFormat === "xlsx" ? "xlsx" : "csv";
      const filename = `statistik-export.${extension}`;

      api
        .get<Blob>(`statistiken/verkauf/export/${this.jobId}/download`, {
          responseType: "blob",
        })
        .then((response) => {
          if (api.isErrorResponse(response.data)) {
            throw new Error("Export file could not be downloaded");
          }

          const url = window.URL.createObjectURL(response.data);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
          window.URL.revokeObjectURL(url);

          this.$emit("complete");
        })
        .catch((error) => {
          console.error("Failed to download file:", error);
          this.$root.$emit("show-error", "Fehler beim Download");
        })
        .finally(() => {
          this.isDownloading = false;
        });
    },
    closeDialog() {
      this.stopPolling();
      this.jobId = null;
      this.jobStatus = null;
      this.isOpen = false;
    },
    formatStatus(status: string): string {
      switch (status) {
        case "pending":
          return "Ausstehend";
        case "running":
          return "Läuft...";
        case "done":
          return "Fertig";
        case "error":
          return "Fehler";
        default:
          return status;
      }
    },
  },
  beforeDestroy() {
    this.stopPolling();
  },
});
